import { createSlice } from '@reduxjs/toolkit';

import { importFetchedStatuses } from '@/mastodon/actions/importer';
import { apiReorderGroupPins } from '@/mastodon/api/groups';
import { apiReorderPinnedStatuses } from '@/mastodon/api/pinned_statuses';
import {
  createAppAsyncThunk,
  createAppSelector,
} from '@/mastodon/store/typed_functions';

interface PinnedStatusesState {
  accountOrder: Record<string, string[]>;
  groupOrder: Record<string, string[]>;
}

const initialState: PinnedStatusesState = {
  accountOrder: {},
  groupOrder: {},
};

export const reorderPinnedStatuses = createAppAsyncThunk(
  'pinnedStatuses/reorder',
  async (
    { ids }: { accountId: string; ids: string[] },
    { dispatch },
  ) => {
    const statuses = await apiReorderPinnedStatuses(ids);
    dispatch(importFetchedStatuses(statuses));
    return statuses.map((status) => status.id);
  },
);

export const reorderGroupPinnedStatuses = createAppAsyncThunk(
  'pinnedStatuses/reorderGroup',
  async (
    { groupId, ids }: { groupId: string; ids: string[] },
    { dispatch },
  ) => {
    const statuses = await apiReorderGroupPins(groupId, ids);
    dispatch(importFetchedStatuses(statuses));
    return statuses.map((status) => status.id);
  },
);

const pinnedStatusesSlice = createSlice({
  name: 'pinnedStatuses',
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder.addCase(reorderPinnedStatuses.pending, (state, action) => {
      state.accountOrder[action.meta.arg.accountId] = action.meta.arg.ids;
    });
    builder.addCase(reorderPinnedStatuses.fulfilled, (state, action) => {
      state.accountOrder[action.meta.arg.accountId] = action.payload;
    });
    builder.addCase(reorderGroupPinnedStatuses.pending, (state, action) => {
      state.groupOrder[action.meta.arg.groupId] = action.meta.arg.ids;
    });
    builder.addCase(reorderGroupPinnedStatuses.fulfilled, (state, action) => {
      state.groupOrder[action.meta.arg.groupId] = action.payload;
    });
  },
});

export const pinnedStatuses = pinnedStatusesSlice.reducer;

export const selectPinnedStatusesOrder = createAppSelector(
  [
    (state) => state.pinnedStatuses.accountOrder,
    (_state, accountId: string) => accountId,
  ],
  (order, accountId): string[] | undefined => order[accountId],
);

export const selectGroupPinnedStatusesOrder = createAppSelector(
  [
    (state) => state.pinnedStatuses.groupOrder,
    (_state, groupId: string) => groupId,
  ],
  (order, groupId): string[] | undefined => order[groupId],
);
